// src/components/scratch/ScratchScore.jsx
import React from "react";
import { formatScore, scoreBand } from "../../services/scratchService";

/**
 * Radial 0..100 "Scratch Score" gauge, drawn as inline SVG (no chart lib —
 * matches how Icon.jsx hand-builds SVG). The score reflects remaining-prize
 * strength from public data; the band label is intentionally about relative
 * value, never about likelihood of winning.
 *
 * Sizes: "sm" (badge), "md" (default), "lg" (detail hero).
 */
export default function ScratchScore({ score, size = "md", showLabel = true }) {
  const px = size === "lg" ? 132 : size === "sm" ? 64 : 96;
  const stroke = size === "lg" ? 11 : size === "sm" ? 6 : 9;
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  const band = scoreBand(score);

  const tone =
    band.tone === "gold"
      ? "var(--gold)"
      : band.tone === "teal"
      ? "var(--teal)"
      : band.tone === "blue"
      ? "var(--blue)"
      : band.tone === "red"
      ? "var(--red)"
      : "var(--txt-3)";

  const r = (px - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (n / 100) * c;
  const fontSize = size === "lg" ? 30 : size === "sm" ? 16 : 22;

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <div style={{ position: "relative", width: px, height: px }}>
        <svg width={px} height={px} viewBox={`0 0 ${px} ${px}`}>
          <circle
            cx={px / 2}
            cy={px / 2}
            r={r}
            fill="none"
            stroke="var(--border-2)"
            strokeWidth={stroke}
          />
          <circle
            cx={px / 2}
            cy={px / 2}
            r={r}
            fill="none"
            stroke={tone}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c - dash}`}
            transform={`rotate(-90 ${px / 2} ${px / 2})`}
            style={{ transition: "stroke-dasharray 600ms ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            className="font-semibold tabular-nums"
            style={{ fontSize, color: "var(--txt)", lineHeight: 1 }}
          >
            {formatScore(score)}
          </span>
          {size !== "sm" && (
            <span className="text-[10px]" style={{ color: "var(--txt-3)" }}>
              / 100
            </span>
          )}
        </div>
      </div>
      {showLabel && (
        <span
          className="text-[11px] font-medium text-center"
          style={{ color: tone, maxWidth: px + 60 }}
        >
          {band.label}
        </span>
      )}
    </div>
  );
}
